import React from "react";

import { Chip } from "@mui/material";
import type { ChipProps } from "@mui/material/Chip";
import type { SxProps, Theme } from "@mui/material/styles";

export type StatusChipValue = "Active" | "Inactive" | "Banned" | "Pending";

export interface StatusChipProps extends Omit<ChipProps, "label" | "size"> {
	status: StatusChipValue;
	label?: string;
	size?: ChipProps["size"];
	height?: number | string;
	fontSize?: number | string;
	sx?: SxProps<Theme>;
}

const getStatusStyles = (status: StatusChipValue) => {
	switch (status) {
		case "Active":
			return {
				backgroundColor: "#e6f6ee",
				color: "#1d7f52",
				borderColor: "#bfe8d1",
			};
		case "Inactive":
			return {
				backgroundColor: "#f2f4f7",
				color: "#5f6b7a",
				borderColor: "#d9dee5",
			};
		case "Banned":
			return {
				backgroundColor: "#fdecec",
				color: "#b42318",
				borderColor: "#f7c0bc",
			};
		case "Pending":
			return {
				backgroundColor: "#fff5dd",
				color: "#9a6700",
				borderColor: "#f2d49b",
			};
		default:
			return {
				backgroundColor: "#f2f4f7",
				color: "#5f6b7a",
				borderColor: "#d9dee5",
			};
	}
};

const StatusChip: React.FC<StatusChipProps> = ({
	status,
	label,
	size = "small",
	height,
	fontSize,
	sx,
	...chipProps
}) => {
	const statusStyles = getStatusStyles(status);
	const resolvedHeight = height ?? (size === "small" ? 18 : 24);
	const resolvedFontSize = fontSize ?? (size === "small" ? "0.7rem" : "0.8rem");

	return (
		<Chip
			{...chipProps}
			label={label ?? status}
			size={size}
			sx={{
				height: resolvedHeight,
				fontSize: resolvedFontSize,
				fontWeight: 600,
				border: "1px solid",
                textTransform: "uppercase",
				...statusStyles,
				"& .MuiChip-label": {
					display: "flex",
					alignItems: "center",
					lineHeight: 1,
					px: 1,
				},
				...sx,
			}}
		/>
	);
};

export default StatusChip;
